import React, { useCallback, useMemo, useState } from 'react';

import { Plus } from '@gravity-ui/icons';
import { Button, Card, Icon, Select, TextArea, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { Field } from 'client/blocks/form/Field/Field';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { lengthConfig } from 'client/lib/validation';
import { ErrorForLanguageForm, LanguageCode, SupportedLanguageFormData } from 'client/types';
import { DEFAULT_EXTERNAL_DESCRIPTION } from 'client/utils/settings/content';
import { getRtlByLanguageCode, isAvailableLanguage } from 'client/utils/settings/languages';
import * as keysetCommon from 'i18n/Common';
import * as languagesKeyset from 'i18n/Language';
import * as settingsKeyset from 'i18n/Settings';
import * as settingsLabelsKeyset from 'i18n/SettingsLabels';
import * as smartHomeFormKeyset from 'i18n/SmartHomeForm';

import styles from './SmartHomeLanguagesEdit.module.scss';

const tSettingsLabels = i18n(settingsLabelsKeyset);
const tSmartHome = i18n(smartHomeFormKeyset);
const tLanguage = i18n(languagesKeyset);
const tSettings = i18n(settingsKeyset);
const tCommon = i18n(keysetCommon);

interface Props {
    isDraft: boolean;
    isTrustedSmartHomeSkill: boolean;
    languages: SupportedLanguageFormData[];
    serializedErrorsForLanguage: string[] | undefined;
    setSupportedLanguages: (value: SupportedLanguageFormData[]) => void;
    disabled: boolean;
}

interface LanguageSetupProps {
    data: SupportedLanguageFormData;
    isDraft: boolean;
    isTrustedSmartHomeSkill: boolean;
    languageOptions: { value: string; content: string }[];
    onFieldChange: (updatedValues: SupportedLanguageFormData) => void;
    errors: ErrorForLanguageForm;
    disabled: boolean;
}

const LanguageDeleteButton: React.FC<{ onClick: () => void; disabled: boolean }> = ({ onClick, disabled }) => {
    const [open, setIsOpen] = useState(false);

    const handleConfirm = () => {
        setIsOpen(false);
        onClick();
    };

    if (disabled) return null;

    return (
        <>
            <Button disabled={disabled} onClick={() => setIsOpen(true)} size="l">
                {tCommon('button-delete')}
            </Button>
            {open && (
                <ModalWithConfirmation
                    onClose={() => setIsOpen(false)}
                    onConfirm={handleConfirm}
                    title={tSettings('header-delete-language')}
                    confirmButtonLabel={tCommon('button-delete')}
                >
                    <>
                        <p>{tSettings('description-delete-language-one')}</p>
                        <p>{tSettings('description-delete-language-two')}</p>
                    </>
                </ModalWithConfirmation>
            )}
        </>
    );
};

const LanguageSetup: React.FC<LanguageSetupProps> = ({
    data,
    isDraft,
    isTrustedSmartHomeSkill,
    languageOptions,
    onFieldChange,
    errors,
    disabled,
}) => {
    const handleLangChange = (selectedLanguage: string) => {
        if (!(selectedLanguage && isAvailableLanguage(selectedLanguage))) {
            return;
        }
        const updatedValues: SupportedLanguageFormData = { languageCode: selectedLanguage };
        const defaultExternalDescription = selectedLanguage && DEFAULT_EXTERNAL_DESCRIPTION?.[selectedLanguage];
        const isCurrentExternalDescriptionOneOfDefaults =
            Object.values(DEFAULT_EXTERNAL_DESCRIPTION).includes(externalSettingsDescription);
        if ((!externalSettingsDescription || isCurrentExternalDescriptionOneOfDefaults) && defaultExternalDescription) {
            updatedValues['externalSettingsDescription'] = defaultExternalDescription;
        }
        onFieldChange(updatedValues);
    };

    const externalSettingsDescription = data.externalSettingsDescription || '';
    const alreadyPublishedDescription = !isDraft && !externalSettingsDescription && data.description;

    return (
        <>
            <Field
                required
                fieldTitle={tSettingsLabels('language')}
                fieldDescription={tSettingsLabels('language-description')}
                error={errors.languageDisplayName}
                containerClassName={styles.smartHomeLanguagesEdit__field}
            >
                <Select
                    className={styles.smartHomeLanguagesEdit__formElement}
                    value={[data.languageCode || '']}
                    onUpdate={(selectedLang) => handleLangChange(selectedLang?.[0])}
                    options={languageOptions}
                    placeholder={tSettingsLabels('select-language')}
                    size="xl"
                    disabled={disabled}
                />
            </Field>
            <Field
                required
                fieldTitle={tSettingsLabels('just-name')}
                fieldDescription={tSmartHome('skill-name')}
                containerClassName={styles.smartHomeLanguagesEdit__field}
                contentClassName={clx(getRtlByLanguageCode(data) && styles.rtl)}
            >
                <TextInput
                    className={styles.smartHomeLanguagesEdit__formElement}
                    value={data.appName}
                    onUpdate={(text) => onFieldChange({ appName: text })}
                    error={errors.appName}
                    autoComplete="none"
                    size="xl"
                    disabled={disabled}
                />
            </Field>
            <Field
                fieldTitle={tSettingsLabels('subtitle')}
                containerClassName={styles.smartHomeLanguagesEdit__field}
                contentClassName={clx(getRtlByLanguageCode(data) && styles.rtl)}
            >
                <TextInput
                    className={styles.smartHomeLanguagesEdit__formElement}
                    value={data.subtitle || ''}
                    onUpdate={(text) => onFieldChange({ subtitle: text })}
                    error={errors.subtitle}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.secondaryTitle - (data.subtitle?.length || 0)}
                        </span>
                    }
                    size="xl"
                    disabled={disabled}
                />
            </Field>
            {alreadyPublishedDescription ? (
                <Field
                    required
                    fieldTitle={tSettingsLabels('description')}
                    containerClassName={styles.smartHomeLanguagesEdit__field}
                    contentClassName={clx(getRtlByLanguageCode(data) && styles.rtl)}
                >
                    <TextArea
                        className={styles.smartHomeLanguagesEdit__formElement}
                        value={alreadyPublishedDescription || ''}
                        error={errors.description}
                        note={
                            <span className={styles.grayNote}>
                                {lengthConfig.description - (alreadyPublishedDescription?.length || 0)}
                            </span>
                        }
                        rows={10}
                        readOnly
                        size="xl"
                    />
                </Field>
            ) : (
                <>
                    <Field
                        required
                        fieldTitle={tSettingsLabels('description-devices-connection')}
                        containerClassName={styles.smartHomeLanguagesEdit__field}
                        contentClassName={clx(getRtlByLanguageCode(data) && styles.rtl)}
                    >
                        <TextArea
                            className={styles.smartHomeLanguagesEdit__formElement}
                            note={
                                <span className={styles.grayNote}>
                                    {lengthConfig.description - (externalSettingsDescription?.length || 0)}
                                </span>
                            }
                            value={externalSettingsDescription}
                            onUpdate={(text) => onFieldChange({ externalSettingsDescription: text })}
                            placeholder={tSmartHome('external-settings-add-language-placeholder')}
                            error={errors.externalSettingsDescription}
                            rows={6}
                            size="xl"
                            disabled={disabled}
                        />
                    </Field>
                    <Field
                        required
                        fieldTitle={tSettingsLabels('description-list-of-supported-devices')}
                        containerClassName={styles.smartHomeLanguagesEdit__field}
                        contentClassName={clx(getRtlByLanguageCode(data) && styles.rtl)}
                    >
                        <TextArea
                            className={styles.smartHomeLanguagesEdit__formElement}
                            value={data.supportedUnitsDescription || ''}
                            onUpdate={(text) => onFieldChange({ supportedUnitsDescription: text })}
                            note={
                                <span className={styles.grayNote}>
                                    {lengthConfig.description - (data.supportedUnitsDescription?.length || 0)}
                                </span>
                            }
                            placeholder={
                                data.languageCode
                                    ? tSmartHome('supported-units-placeholder')
                                    : tSmartHome('supported-units-add-language-placeholder')
                            }
                            error={errors.supportedUnitsDescription}
                            rows={6}
                            size="xl"
                            disabled={disabled}
                        />
                    </Field>
                    <Field
                        required={isTrustedSmartHomeSkill}
                        fieldTitle={tSettingsLabels('description-support-url')}
                        error={errors.supportServiceUrl}
                        containerClassName={styles.smartHomeLanguagesEdit__field}
                    >
                        <TextInput
                            className={styles.smartHomeLanguagesEdit__formElement}
                            value={data.supportServiceUrl}
                            onUpdate={(text) => onFieldChange({ supportServiceUrl: text })}
                            type="url"
                            size="xl"
                            disabled={disabled}
                        />
                    </Field>
                </>
            )}
        </>
    );
};

export const SmartHomeLanguagesEdit: React.FC<Props> = ({
    isDraft,
    isTrustedSmartHomeSkill,
    languages,
    serializedErrorsForLanguage,
    setSupportedLanguages,
    disabled,
}) => {
    const onChangeTemplate =
        (updatedLangIndex: number) =>
        (updatedValues: SupportedLanguageFormData = {}) => {
            const result: SupportedLanguageFormData[] = languages.map((langSettingData, langSettingIndex) => {
                const newEntries = updatedLangIndex === langSettingIndex ? updatedValues : {};
                return { ...langSettingData, supportServiceUrlIsRequired: isTrustedSmartHomeSkill, ...newEntries };
            });
            setSupportedLanguages(result);
        };

    const onRemoveLanguage = useCallback(
        (idx: number) => () => {
            const result: SupportedLanguageFormData[] = languages.map((lang) => Object.assign({}, lang));
            result.splice(idx, 1);
            setSupportedLanguages(result);
        },
        [setSupportedLanguages, languages],
    );

    const currentErrors = useMemo<ErrorForLanguageForm[]>(() => {
        if (serializedErrorsForLanguage === undefined || serializedErrorsForLanguage.length !== languages.length) {
            return languages.map(() => ({}));
        }

        return serializedErrorsForLanguage.map((value) => JSON.parse(value) as ErrorForLanguageForm);
    }, [serializedErrorsForLanguage, languages]);

    const languageOptions = useMemo(() => {
        return Object.keys(languagesKeyset.en).map((value) => ({ value, content: tLanguage(value as LanguageCode) }));
    }, []);

    const handleAddLanguage = useCallback(() => {
        const languagesCopy: SupportedLanguageFormData[] = languages.map((lang) => Object.assign({}, lang));
        languagesCopy.push({});
        setSupportedLanguages(languagesCopy);
    }, [languages, setSupportedLanguages]);

    const languageLimitIsReached = languages.length === languageOptions.length;

    return (
        <>
            {languages.map((lang, idx) => {
                return (
                    <Card key={idx} className={styles.smartHomeLanguagesEdit__card}>
                        {idx !== 0 && (
                            <header className={styles.smartHomeLanguagesEdit__header}>
                                <h4>{tSmartHome('additional-language')}</h4>
                                <LanguageDeleteButton disabled={disabled} onClick={onRemoveLanguage(idx)} />
                            </header>
                        )}
                        <LanguageSetup
                            isDraft={isDraft}
                            isTrustedSmartHomeSkill={isTrustedSmartHomeSkill}
                            onFieldChange={onChangeTemplate(idx)}
                            data={lang}
                            languageOptions={languageOptions}
                            errors={currentErrors[idx]}
                            disabled={disabled}
                        />
                    </Card>
                );
            })}
            <Button
                className={styles.smartHomeLanguagesEdit__addLanguageButton}
                onClick={handleAddLanguage}
                disabled={disabled || languageLimitIsReached}
                size="l"
            >
                <Icon data={Plus} />
                {tSmartHome('add-language')}
            </Button>
        </>
    );
};
